import { ReactElement, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { commentPaletteColorOptions } from '@constants'
import { reject, sortBy, toNumber } from 'lodash-es'
import {
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { DeleteButton, SwitchLabeled } from '@ui/actions'
import { CoordinatePointInput, createHighPrecisionPointManipulationConfig } from '@ui/forms'
import { Box } from '@ui/structure'
import { PlanType, roofStoreyKey, Tapeline, useModelStore } from '@editorStores'

export type TapelineAttributesPatch = Partial<
  Pick<Tapeline, 'showLengthLabel' | 'text' | 'style' | 'start' | 'end' | 'planConfig'>
>

const planTypeOptions: { value: PlanType; label: string }[] = [
  { value: 'PositionPlan', label: 'Positionsplan' },
  { value: 'StiffeningPlan', label: 'Aussteifungsplan' },
  { value: 'LoadPlan', label: 'Lastplan' },
]

interface Props {
  selectedLine?: Tapeline
  onUpdateSelectedLine: (patch: TapelineAttributesPatch) => void
  onDeleteSelectedLine: () => void
}

interface TapelineCoordinateFormValues {
  start: { x: number; y: number; z: number }
  end: { x: number; y: number; z: number }
}

const TapelineSettings = ({
  selectedLine,
  onUpdateSelectedLine,
  onDeleteSelectedLine,
}: Props): ReactElement => {
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const coordinateForm = useForm<TapelineCoordinateFormValues>({
    defaultValues: {
      start: {
        x: selectedLine?.start.x ?? 0,
        y: selectedLine?.start.y ?? 0,
        z: selectedLine?.start.z ?? 0,
      },
      end: {
        x: selectedLine?.end.x ?? 0,
        y: selectedLine?.end.y ?? 0,
        z: selectedLine?.end.z ?? 0,
      },
    },
  })
  const { reset } = coordinateForm

  useEffect(() => {
    if (!selectedLine) return
    reset({
      start: {
        x: selectedLine.start.x,
        y: selectedLine.start.y,
        z: selectedLine.start.z,
      },
      end: {
        x: selectedLine.end.x,
        y: selectedLine.end.y,
        z: selectedLine.end.z,
      },
    })
  }, [reset, selectedLine])

  const sortedStoreys = useMemo(() => {
    const nonRoof = reject(Array.from(availableStoreys), storey => storey === roofStoreyKey)
    return [...sortBy(nonRoof, storey => toNumber(storey)), roofStoreyKey]
  }, [availableStoreys])

  const isStoreySelected = (storey: string): boolean => {
    if (!selectedLine) return false
    return selectedLine.planConfig.storeysForPlans.includes(storey)
  }

  const toggleStoreyForPlans = (storey: string): void => {
    if (!selectedLine) return

    const current = selectedLine.planConfig.storeysForPlans
    const isSelected = isStoreySelected(storey)
    const nextStoreys = isSelected
      ? current.filter(value => value !== storey)
      : [...current, storey]

    onUpdateSelectedLine({
      planConfig: {
        ...selectedLine.planConfig,
        storeysForPlans: nextStoreys,
      },
    })
  }

  const togglePlanType = (planType: PlanType): void => {
    if (!selectedLine) return

    const current = selectedLine.planConfig.planTypes
    const nextPlanTypes = current.includes(planType)
      ? current.filter(value => value !== planType)
      : [...current, planType]

    onUpdateSelectedLine({
      planConfig: {
        ...selectedLine.planConfig,
        planTypes: nextPlanTypes,
      },
    })
  }

  const updateLineCoordinate = (
    point: 'start' | 'end',
    axis: 'x' | 'y' | 'z',
    value: number | null,
  ): void => {
    if (!selectedLine) return
    if (value === null || !Number.isFinite(value)) return

    const currentPoint = point === 'start' ? selectedLine.start : selectedLine.end
    const nextPoint = new ImmutableVector3(
      axis === 'x' ? value : currentPoint.x,
      axis === 'y' ? value : currentPoint.y,
      axis === 'z' ? value : currentPoint.z,
    )

    onUpdateSelectedLine({
      [point]: nextPoint,
    })
  }

  const updateLinePoint = (point: 'start' | 'end', x: number, y: number, z: number): void => {
    onUpdateSelectedLine({
      [point]: new ImmutableVector3(x, y, z),
    })
  }

  if (!selectedLine) {
    return (
      <Typography variant="body2" color="text.secondary">
        Wähle ein Maßband aus, um seine Attribute zu bearbeiten.
      </Typography>
    )
  }

  return (
    <Stack spacing={1} data-cy="tapeline-settings">
      <FormProvider {...coordinateForm}>
        <CoordinatePointInput
          name="start"
          label="Start"
          pointManipulation={createHighPrecisionPointManipulationConfig()}
          onChangeX={value => updateLineCoordinate('start', 'x', value)}
          onChangeY={value => updateLineCoordinate('start', 'y', value)}
          onChangeZ={value => updateLineCoordinate('start', 'z', value)}
          onReplace={(x, y, z) => updateLinePoint('start', x, y, z)}
          actionsDefaultExpanded
        />

        <CoordinatePointInput
          name="end"
          label="Ende"
          pointManipulation={createHighPrecisionPointManipulationConfig()}
          onChangeX={value => updateLineCoordinate('end', 'x', value)}
          onChangeY={value => updateLineCoordinate('end', 'y', value)}
          onChangeZ={value => updateLineCoordinate('end', 'z', value)}
          onReplace={(x, y, z) => updateLinePoint('end', x, y, z)}
          actionsDefaultExpanded
        />
      </FormProvider>
      <SwitchLabeled
        label="Länge anzeigen"
        checked={selectedLine.showLengthLabel}
        onChange={event => onUpdateSelectedLine({ showLengthLabel: event.target.checked })}
      />

      <TextField
        size="small"
        label="Text Anfang"
        value={selectedLine.text?.start || ''}
        onChange={event =>
          onUpdateSelectedLine({
            text: {
              ...selectedLine.text,
              start: event.target.value || undefined,
            },
          })
        }
      />

      <TextField
        size="small"
        label="Text Mitte"
        value={selectedLine.text?.center || ''}
        onChange={event =>
          onUpdateSelectedLine({
            text: {
              ...selectedLine.text,
              center: event.target.value || undefined,
            },
          })
        }
      />

      <TextField
        size="small"
        label="Text Ende"
        value={selectedLine.text?.end || ''}
        onChange={event =>
          onUpdateSelectedLine({
            text: {
              ...selectedLine.text,
              end: event.target.value || undefined,
            },
          })
        }
      />

      <Stack direction="row" spacing={1}>
        <FormControl size="small">
          <InputLabel id="tapeline-color-label">Farbe</InputLabel>
          <Select
            labelId="tapeline-color-label"
            value={selectedLine.style.color}
            label="Farbe"
            onChange={event =>
              onUpdateSelectedLine({
                style: {
                  ...selectedLine.style,
                  color: event.target.value,
                },
              })
            }
          >
            {commentPaletteColorOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      backgroundColor: option.value,
                    }}
                  />
                  <Typography variant="body2">{option.label}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Dicke"
          type="number"
          value={selectedLine.style.thickness}
          inputProps={{ min: 1, step: 0.5 }}
          sx={{ width: 80 }}
          onChange={event =>
            onUpdateSelectedLine({
              style: {
                ...selectedLine.style,
                thickness: Number(event.target.value) || 1,
              },
            })
          }
        />

        <FormControl size="small">
          <InputLabel id="tapeline-line-style-label">Linienstil</InputLabel>
          <Select
            labelId="tapeline-line-style-label"
            value={selectedLine.style.lineStyle}
            label="Linienstil"
            onChange={event =>
              onUpdateSelectedLine({
                style: {
                  ...selectedLine.style,
                  lineStyle: event.target.value as 'solid' | 'dashed' | 'dashed-long-gap',
                },
              })
            }
          >
            <MenuItem value="solid">
              <Typography fontWeight="bold" fontFamily="monospace" sx={{ whiteSpace: 'pre' }}>
                -----
              </Typography>
            </MenuItem>
            <MenuItem value="dashed">
              <Typography fontWeight="bold" fontFamily="monospace" sx={{ whiteSpace: 'pre' }}>
                - - -
              </Typography>
            </MenuItem>
            <MenuItem value="dashed-long-gap">
              <Typography fontWeight="bold" fontFamily="monospace" sx={{ whiteSpace: 'pre' }}>
                {'-   -'}
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Divider />

      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          Stockwerke für Planerstellung
        </Typography>
        <FormGroup row>
          {sortedStoreys.map(storey => (
            <FormControlLabel
              key={storey}
              control={
                <Checkbox
                  size="small"
                  checked={isStoreySelected(storey)}
                  onChange={() => toggleStoreyForPlans(storey)}
                />
              }
              label={<Typography variant="body2">{storeyToLabel(storey)}</Typography>}
            />
          ))}
        </FormGroup>
      </Stack>

      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          In Plänen anzeigen
        </Typography>
        <Stack>
          {planTypeOptions.map(({ value, label }) => (
            <FormControlLabel
              key={value}
              control={
                <Checkbox
                  size="small"
                  checked={selectedLine.planConfig.planTypes.includes(value)}
                  onChange={() => togglePlanType(value)}
                />
              }
              label={<Typography variant="body2">{label}</Typography>}
            />
          ))}
        </Stack>
      </Stack>

      <DeleteButton onClick={onDeleteSelectedLine} size="small" data-cy="btn-tapeline-delete">
        Entfernen
      </DeleteButton>
    </Stack>
  )
}

export default TapelineSettings
